import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    Stack,
} from "@mui/material";
import React, { Dispatch, SetStateAction, useState } from "react";
import { useRecordContext, useRefresh } from "react-admin";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "./CustomSnackBar";
import { CustomerService } from "../../services/CustomerService";

interface InputProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
}

const customerService = new CustomerService();

const ChangeCustomerPhoneDialog = (props: InputProps) => {
    const record = useRecordContext();
    const refresh = useRefresh();
    const customerId = Number(record?.id);
    const currentMobile = record?.mobile ?? "";

    const [newMobile, setNewMobile] = useState<string>("");
    const [submitting, setSubmitting] = useState<boolean>(false);

    const handleClose = () => {
        if (submitting) return;
        setNewMobile("");
        props.setOpen(false);
    };

    const handleSubmit = async () => {
        const trimmed = newMobile.trim();
        if (!/^[0-9]{10}$/.test(trimmed)) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Enter a valid 10-digit mobile number",
                type: CustomSnackbarTypes.ERROR,
            });
            return;
        }
        if (trimmed === currentMobile) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "New number is the same as the current number",
                type: CustomSnackbarTypes.ERROR,
            });
            return;
        }
        setSubmitting(true);
        try {
            await customerService.updateCustomerPhone(customerId, trimmed);
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Phone number updated. If it belonged to another customer, this customer has been added to their household and given access to their coupons.",
                type: CustomSnackbarTypes.SUCCESS,
            });
            setNewMobile("");
            props.setOpen(false);
            refresh();
        } catch (e) {
            // HttpClient error interceptor already surfaces the snackbar.
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={props.open} onClose={handleClose} scroll={"paper"} fullWidth maxWidth={"sm"}>
            <DialogTitle>Change Phone Number</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <TextField
                        label="Current number"
                        value={currentMobile}
                        InputProps={{ readOnly: true }}
                        fullWidth
                    />
                    <TextField
                        label="New 10-digit number"
                        value={newMobile}
                        onChange={(e) => setNewMobile(e.target.value)}
                        placeholder="9876543210"
                        inputProps={{ inputMode: "numeric", maxLength: 10 }}
                        fullWidth
                        autoFocus
                    />
                    <Alert severity="info">
                        If the new number is unused, the customer&apos;s mobile is updated and their active bookings are migrated to it.
                        If it already belongs to another customer, this customer is linked into that household and gains access to the existing customer&apos;s active coupons.
                    </Alert>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color={"success"}
                    onClick={handleSubmit}
                    size={"large"}
                    disabled={submitting}
                >
                    {submitting ? "Updating..." : "Submit"}
                </Button>
                <Button
                    variant="contained"
                    color={"error"}
                    onClick={handleClose}
                    size={"large"}
                    disabled={submitting}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangeCustomerPhoneDialog;
